import { Head, Link, router, usePage } from "@inertiajs/react"
import { toQueryString } from "@/lib/queryParams"
import { useTableDensity } from "@/lib/useTableDensity"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import AddIcon from "@mui/icons-material/Add"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteIcon from "@mui/icons-material/Delete"
import DownloadIcon from "@mui/icons-material/Download"
import EditIcon from "@mui/icons-material/Edit"
import HistoryIcon from "@mui/icons-material/History"
import RefreshIcon from "@mui/icons-material/Refresh"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import RecordHistoryDrawer from "@/pages/history/RecordHistoryDrawer"
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material"
import dayjs from "dayjs"
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_PaginationState,
    type MRT_SortingState,
    useMaterialReactTable,
} from "material-react-table"
import { ElementType, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

type SortDirection = "asc" | "desc"

interface IExtrusion {
    id: number
    code: string
    version: number
    date: string
    updated_at: string
    orders_count: number
}

type Props = {
    entries: IExtrusion[]
    search: string
    sort: string
    direction: SortDirection
    pagination: { current_page: number; total_pages: number; total_count: number; per_page: number }
}

function DeleteDialog({ entry, onClose, tableParams }: {
    entry: IExtrusion | null
    onClose: () => void
    tableParams: Record<string, string | number>
}) {
    const { t } = useTranslation()
    const [processing, setProcessing] = useState(false)
    const [snapshot, setSnapshot] = useState<IExtrusion | null>(null)

    const handleDelete = () => {
        if (!snapshot) return
        setProcessing(true)
        const query = new URLSearchParams(tableParams as Record<string, string>).toString()
        router.delete(`/extrusions/${snapshot.id}?${query}`, {
            preserveScroll: true,
            onFinish: () => { setProcessing(false); onClose() },
        })
    }

    return (
        <Dialog open={entry !== null} onClose={onClose} maxWidth="xs" fullWidth
            slotProps={{ transition: { onEnter: () => setSnapshot(entry) } }}>
            <DialogTitle>{t("extrusion.deleteTitle")}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t("extrusion.deleteText", { code: snapshot?.code })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose} disabled={processing} size="small">{t("common.cancel")}</Button>
                <Button color="error" variant="contained" size="small" onClick={handleDelete} disabled={processing}>
                    {processing ? t("common.deleting") : t("common.delete")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default function ExtrusionsIndex() {
    const { entries, search: initialSearch, sort, direction, pagination } = usePage<Props>().props
    const { t } = useTranslation()

    const [deleteEntry, setDeleteEntry] = useState<IExtrusion | null>(null)
    const [historyTarget, setHistoryTarget] = useState<{ type: string; id: number } | null>(null)
    const [reloading, setReloading] = useState(false)
    const { density, onDensityChange } = useTableDensity("extrusions")
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const defaultSort = sort ?? "date"
    const defaultDirection = direction ?? "desc"

    const [sorting, setSorting] = useState<MRT_SortingState>(
        [{ id: defaultSort, desc: defaultDirection === "desc" }]
    )
    const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
        pageIndex: (pagination?.current_page ?? 1) - 1,
        pageSize: pagination?.per_page ?? 25,
    })
    const [globalFilter, setGlobalFilter] = useState(initialSearch ?? "")

    useEffect(() => { setGlobalFilter(initialSearch ?? "") }, [initialSearch])
    useEffect(() => { setSorting([{ id: sort ?? "date", desc: (direction ?? "desc") === "desc" }]) }, [sort, direction])
    useEffect(() => {
        setPaginationState({ pageIndex: (pagination?.current_page ?? 1) - 1, pageSize: pagination?.per_page ?? 25 })
    }, [pagination?.current_page, pagination?.per_page])

    useEffect(() => {
        const key = "scroll_extrusions"
        const saved = sessionStorage.getItem(key)
        if (saved) {
            sessionStorage.removeItem(key)
            requestAnimationFrame(() => { window.scrollTo({ top: parseInt(saved) }) })
        }
        return router.on("before", () => { sessionStorage.setItem(key, String(window.scrollY)) })
    }, [])

    const visit = useCallback((params: Record<string, string | number>) => {
        router.get("/extrusions", params, { preserveState: true, preserveScroll: true })
    }, [])

    const tableParams = useMemo(() => ({
        search: globalFilter,
        sort: sorting[0]?.id ?? "date",
        direction: (sorting[0]?.desc ? "desc" : "asc") as SortDirection,
        page: paginationState.pageIndex + 1,
        per_page: paginationState.pageSize,
    }), [globalFilter, sorting, paginationState])

    const handleDuplicate = useCallback((entry: IExtrusion) => {
        const query = toQueryString(tableParams)
        router.post(`/extrusions/${entry.id}/duplicate?${query}`, {}, { preserveScroll: true })
    }, [tableParams])

    const columns = useMemo<MRT_ColumnDef<IExtrusion>[]>(() => [
        {
            accessorKey: "code",
            header: t("extrusion.form.code"),
            size: 160,
            Cell: ({ cell }) => (
                <span style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{cell.getValue<string>()}</span>
            ),
        },
        { accessorKey: "version", header: t("extrusion.form.version"), size: 100 },
        {
            accessorKey: "date",
            header: t("extrusion.form.date"),
            size: 120,
            Cell: ({ cell }) => cell.getValue<string>() ? dayjs(cell.getValue<string>()).format("DD-MM-YYYY") : "—",
        },
        {
            accessorKey: "orders_count",
            header: t("extrusion.ordersCount"),
            size: 80,
            enableSorting: false,
            muiTableBodyCellProps: { align: "center" },
        },
        {
            accessorKey: "updated_at",
            header: t("common.updatedAt"),
            size: 160,
            Cell: ({ cell }) => dayjs(cell.getValue<string>()).format("DD-MM-YYYY HH:mm"),
        },
    ], [t])

    const table = useMaterialReactTable({
        columns,
        data: entries ?? [],
        enableRowActions: true,
        positionActionsColumn: "last",
        enableColumnFilters: false,
        enableFullScreenToggle: false,
        initialState: { showGlobalFilter: !!initialSearch },
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        rowCount: pagination?.total_count ?? 0,
        onGlobalFilterChange: (updater) => {
            const val = typeof updater === "function" ? updater(globalFilter) : (updater ?? "")
            setGlobalFilter(val)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                visit({
                    search: val,
                    sort: sorting[0]?.id ?? "date",
                    direction: sorting[0]?.desc ? "desc" : "asc",
                    page: 1,
                    per_page: paginationState.pageSize,
                })
            }, 400)
        },
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater(paginationState) : updater
            setPaginationState(next)
            visit({
                search: globalFilter,
                sort: sorting[0]?.id ?? "date",
                direction: sorting[0]?.desc ? "desc" : "asc",
                page: next.pageIndex + 1,
                per_page: next.pageSize,
            })
        },
        onSortingChange: (updater) => {
            const next = typeof updater === "function" ? updater(sorting) : updater
            setSorting(next)
            visit({
                search: globalFilter,
                sort: next[0]?.id ?? "date",
                direction: next[0]?.desc ? "desc" : "asc",
                page: 1,
                per_page: paginationState.pageSize,
            })
        },
        muiPaginationProps: {
            rowsPerPageOptions: [25, 50, 100, 200],
            showFirstButton: true,
            showLastButton: true,
        },
        renderRowActions: ({ row }) => {
            const editUrl = `/extrusions/${row.original.id}/edit?${toQueryString(tableParams)}`
            return (
                <Box sx={{ display: "flex" }}>
                    <Tooltip title={t("common.edit")}>
                        <IconButton color="info" size="small" component={Link as ElementType} href={editUrl}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.duplicate")}>
                        <IconButton color="warning" size="small" onClick={() => handleDuplicate(row.original)}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.downloadExcel")}>
                        <IconButton color="success" size="small" component="a" href={`/extrusions/${row.original.id}/download_excel`}>
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("history.openRecordHistory")}>
                        <IconButton size="small" onClick={() => setHistoryTarget({ type: "Extrusion", id: row.original.id })}>
                            <HistoryIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                        <IconButton color="error" size="small" onClick={() => setDeleteEntry(row.original)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        },
        onDensityChange,
        state: {
            showProgressBars: reloading,
            pagination: paginationState,
            globalFilter,
            sorting,
            density,
        },
    })

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Head title={t("modules.extrusion")} />

            <Box sx={{ mb: 2 }}>
                <Button LinkComponent={Link} href="/" size="small" startIcon={<ArrowBackIcon />} color="inherit">
                    {t("common.home")}
                </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("modules.extrusion")}</Typography>
                    <Tooltip title={t("common.reload")}>
                        <IconButton size="small" onClick={() => { setReloading(true); router.reload({ onFinish: () => setReloading(false) }) }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button variant="outlined" LinkComponent={Link} href="/orders" size="small"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        sx={{ color: "text.primary", borderColor: "text.primary" }}>
                        {t("modules.orders")}
                    </Button>
                    <Button variant="outlined" LinkComponent={Link} href="/exports" size="small"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        sx={{ color: "text.primary", borderColor: "text.primary" }}>
                        {t("modules.exports")}
                    </Button>
                    <Button
                        LinkComponent={Link}
                        href="/history?module=extrusions"
                        startIcon={<HistoryIcon />}
                        variant="outlined"
                        size="small"
                    >
                        {t("history.openHistory")}
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} LinkComponent={Link} href="/extrusions/new" size="small">
                        {t("extrusion.new")}
                    </Button>
                </Box>
            </Box>

            <MaterialReactTable table={table} />

            <DeleteDialog entry={deleteEntry} onClose={() => setDeleteEntry(null)} tableParams={tableParams} />
            <RecordHistoryDrawer
                open={historyTarget !== null}
                onClose={() => setHistoryTarget(null)}
                type={historyTarget?.type ?? null}
                id={historyTarget?.id ?? null}
            />
        </Container>
    )
}
